import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface AuthContextType {
  token: string | null;
  userId: number | null;
  userName: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [userId, setUserId] = useState<number | null>(
    localStorage.getItem('userId') ? parseInt(localStorage.getItem('userId')!) : null
  );
  const [userName, setUserName] = useState<string | null>(localStorage.getItem('userName'));

  // Configurar interceptor de axios para manejar sesiones expiradas
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Si recibimos un 401 (No autorizado), la sesión ha expirado
        if (error.response && error.response.status === 401) {
          // Limpiar token y redirigir al login
          setToken(null);
          setUserId(null);
          setUserName(null);
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('userName');

          // Redirigir al login si no estamos ya allí
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup: remover el interceptor cuando el componente se desmonte
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username,
        password,
      });
      const { access_token, user_id, user_name } = response.data;
      setToken(access_token);
      setUserId(user_id);
      setUserName(user_name);
      localStorage.setItem('token', access_token);
      localStorage.setItem('userId', user_id.toString());
      localStorage.setItem('userName', user_name);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(
          `${API_URL}/auth/logout`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setToken(null);
      setUserId(null);
      setUserName(null);
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
    }
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ token, userId, userName, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};